import React, { useState, useEffect } from 'react';
import { coordinatorService, countryService, stateService, districtService, universityService, collegeService } from '../../services/api';
import './AdminPage.css';

function AdminCoordinators() {
  const [coordinators, setCoordinators] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoordinator, setEditingCoordinator] = useState(null);
  const [formData, setFormData] = useState({
    Name: '',
    Code: '',
    Password: '',
    MobileNo: '',
    Email: '',
    Address: '',
    Location: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    UniversityId: '',
    CollegeId: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      loadStates();
    }
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      loadDistricts();
    }
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await coordinatorService.getCoordinators();
      setCoordinators(data);
    } catch (error) {
      alert('Error loading coordinators: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [countriesData, universitiesData, collegesData] = await Promise.all([
        countryService.getCountries(),
        universityService.getUniversities(),
        collegeService.getColleges()
      ]);
      setCountries(countriesData);
      setUniversities(universitiesData);
      setColleges(collegesData);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const loadStates = async () => {
    try {
      const data = await stateService.getStates();
      setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)));
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const data = await districtService.getDistricts();
      setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleAdd = () => {
    setEditingCoordinator(null);
    setFormData({
      Name: '',
      Code: '',
      Password: '',
      MobileNo: '',
      Email: '',
      Address: '',
      Location: '',
      CountryId: '',
      StateId: '',
      DistrictId: '',
      UniversityId: '',
      CollegeId: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (coordinator) => {
    setEditingCoordinator(coordinator);
    setFormData({
      Id: coordinator.Id,
      Name: coordinator.Name || '',
      Code: coordinator.Code || '',
      Password: '',
      MobileNo: coordinator.MobileNo || '',
      Email: coordinator.Email || '',
      Address: coordinator.Address || '',
      Location: coordinator.Location || '',
      CountryId: coordinator.CountryId || '',
      StateId: coordinator.StateId || '',
      DistrictId: coordinator.DistrictId || '',
      UniversityId: coordinator.UniversityId || '',
      CollegeId: coordinator.CollegeId || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coordinator?')) {
      try {
        await coordinatorService.deleteCoordinator(id);
        loadData();
      } catch (error) {
        alert('Error deleting coordinator: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await coordinatorService.saveCoordinator(formData);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving coordinator: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Coordinators Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add Coordinator</button>
      </div>

      {loading && !coordinators.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coordinators.map((coordinator) => (
                <tr key={coordinator.Id}>
                  <td>{coordinator.Name}</td>
                  <td>{coordinator.Code}</td>
                  <td>{coordinator.Email}</td>
                  <td>{coordinator.MobileNo}</td>
                  <td>{coordinator.Location}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(coordinator)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(coordinator.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCoordinator ? 'Edit Coordinator' : 'Add Coordinator'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.Code}
                    onChange={(e) => setFormData({ ...formData, Code: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password {editingCoordinator ? '' : '*'}</label>
                  <input
                    type="password"
                    value={formData.Password}
                    onChange={(e) => setFormData({ ...formData, Password: e.target.value })}
                    required={!editingCoordinator}
                    placeholder={editingCoordinator ? "Leave blank to keep current password" : ""}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.Email}
                    onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Mobile No</label>
                  <input
                    type="text"
                    value={formData.MobileNo}
                    onChange={(e) => setFormData({ ...formData, MobileNo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.Address}
                    onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.Location}
                    onChange={(e) => setFormData({ ...formData, Location: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.CountryId}
                    onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.Id} value={country.Id}>{country.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.StateId}
                    onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })}
                    disabled={!formData.CountryId}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.Id} value={state.Id}>{state.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.DistrictId}
                    onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })}
                    disabled={!formData.StateId}
                  >
                    <option value="">Select District</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>University</label>
                  <select
                    value={formData.UniversityId}
                    onChange={(e) => setFormData({ ...formData, UniversityId: e.target.value })}
                  >
                    <option value="">Select University</option>
                    {universities.map((university) => (
                      <option key={university.Id} value={university.Id}>{university.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>College</label>
                  <select
                    value={formData.CollegeId}
                    onChange={(e) => setFormData({ ...formData, CollegeId: e.target.value })}
                  >
                    <option value="">Select College</option>
                    {colleges.map((college) => (
                      <option key={college.Id} value={college.Id}>{college.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCoordinators;

